import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import { ChatList } from './ChatList';
import { MessageList } from './MessageList';
import { MessageInput, MessageInputProps } from './MessageInput';
import { RecipientSelector } from './RecipientSelector';
import { ChatLoader } from './ChatLoader';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useChatMessagesSimple } from '../hooks/useChatMessagesSimple';
import { useChatNotifications } from '../hooks/useChatNotifications';
import { Chat, Message, RecipientType, ChatWidgetProps } from '../types';
import { logger } from '../../../shared/utils/logger';
import { getStatusText, getStatusIcon } from '../utils/statusUtils';
import { useOrdersStore } from '../../../shared/store/ordersStore';
import { useAuth } from '../../../shared/auth/store';
import '../../../shared/styles/order-detail.css';
import '../styles/ChatWidget.css';

// Компонент для переключения звука
const SoundToggleButton: React.FC = () => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('chat_sound_enabled') !== 'false';
  });

  const toggleSound = useCallback(() => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('chat_sound_enabled', newState.toString());
    
    // Воспроизводим тестовый звук при включении
    if (newState) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
        
        const volume = parseFloat(localStorage.getItem('chat_sound_volume') || '0.1');
        gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(volume * 0.1, audioContext.currentTime + 0.2);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (error) {
        console.log('Не удалось воспроизвести тестовый звук:', error);
      }
    }
  }, [soundEnabled]);

  return (
    <button
      onClick={toggleSound}
      className="chat-widget-sound-toggle"
      title={soundEnabled ? 'Отключить звук уведомлений' : 'Включить звук уведомлений'}
      style={{
        background: 'none',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: soundEnabled ? '#10b981' : '#6b7280',
        transition: 'color 0.2s'
      }}
    >
      {soundEnabled ? (
        <Volume2 size={16} />
      ) : (
        <VolumeX size={16} />
      )}
    </button>
  );
};

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  orderId,
  className = '',
  onChatSelect,
  onMessageSent,
  onUnreadCountChange
}) => {
  const navigate = useNavigate();
  const { isTelegramMode } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [activeView, setActiveView] = useState<'list' | 'chat'>('list');
  const [selectedRecipient, setSelectedRecipient] = useState<RecipientType | null>(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [userReturnedToList, setUserReturnedToList] = useState(false); // Флаг что пользователь вручную вернулся к списку
  const [hasAutoOpenedOrderChat, setHasAutoOpenedOrderChat] = useState(false); // Флаг что чат заказа уже был автоматически открыт
  // Управление скроллом страницы и Telegram Mini App
  useEffect(() => {
    if (isVisible) {
      // Для Telegram Mini App - отключаем вертикальные свайпы
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.disableVerticalSwipes();
      }
      
      // Добавляем класс для блокировки скролла страницы
      document.body.classList.add('chat-widget-modal-open');
    } else {
      // Восстанавливаем свайпы когда виджет закрыт
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.enableVerticalSwipes();
      }
      
      // Убираем класс блокировки скролла
      document.body.classList.remove('chat-widget-modal-open');
    }

    return () => {
      // Cleanup при размонтировании
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.enableVerticalSwipes();
      }
      document.body.classList.remove('chat-widget-modal-open');
    };
  }, [isVisible]);

  // Хуки для управления чатами и сообщениями
  const {
    chats,
    activeChat,
    messages,
    isLoading,
    messagesHasMore,
    isLoadingMore,
    loadChats,
    selectChat,
    sendMessage,
    sendFileMessage,
    markAsRead,
    findOrderChat,
    clearActiveChat,
    loadMoreMessages
  } = useChatMessagesSimple();

  // 🔥 ИСПОЛЬЗУЕМ ГЛОБАЛЬНЫЙ СЧЕТЧИК ИЗ STORE
  const { counts, updateUnreadMessagesCount, setChatWidgetActive, setActiveChatId } = useOrdersStore();
  const unreadCount = counts.unread_messages;

  // 🔥 ПЕРЕДАЕМ СЧЕТЧИК НЕПРОЧИТАННЫХ В КОЛЛБЕК
  useEffect(() => {
    console.log('🔥 ChatWidget: unreadCount изменился на:', unreadCount);
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  // 🔥 ОТСЛЕЖИВАЕМ СОСТОЯНИЕ ВИДЖЕТА
  useEffect(() => {
    setChatWidgetActive(isVisible);
  }, [isVisible, setChatWidgetActive]);

  // 🎯 СБРАСЫВАЕМ ФЛАГИ при изменении orderId для повторного автоматического открытия
  useEffect(() => {
    if (orderId) {
      setUserReturnedToList(false);
      setHasAutoOpenedOrderChat(false);
    }
  }, [orderId]);

  // 🎯 ОБРАБОТЧИК СОБЫТИЯ openChatWidget для автоматического открытия чата
  useEffect(() => {
    const handleOpenChatWidget = (event: CustomEvent) => {
      const { orderId: targetOrderId, autoOpen } = event.detail || {};
      
      if (autoOpen && targetOrderId) {
        // Открываем виджет
        setIsVisible(true);
        
        // 🎯 СБРАСЫВАЕМ ФЛАГИ чтобы разрешить автоматическое открытие
        setUserReturnedToList(false);
        setHasAutoOpenedOrderChat(false);
        
        // Основной useEffect сам найдет и откроет чат при изменении orderId
      }
    };

    window.addEventListener('openChatWidget', handleOpenChatWidget as EventListener);
    
    return () => {
      window.removeEventListener('openChatWidget', handleOpenChatWidget as EventListener);
    };
  }, []);

  // 🎯 ЭКСПОРТИРУЕМ CHATWIDGET В WINDOW ДЛЯ СОВМЕСТИМОСТИ С ОРИГИНАЛОМ
  useEffect(() => {
    const chatWidgetAPI = {
      showOrderChat: (orderId: string) => {
        window.dispatchEvent(new CustomEvent('openChatWidget', { 
          detail: { orderId: orderId, autoOpen: true }
        }));
      },
      show: () => setIsVisible(true),
      hide: () => setIsVisible(false),
      isVisible: () => isVisible
    };

    (window as any).chatWidget = chatWidgetAPI;
    
    return () => {
      delete (window as any).chatWidget;
    };
  }, [selectChat, loadChats, isVisible]);



  // WebSocket для реального времени (только для статуса соединения)
  const { isConnected } = useChatWebSocket({
    // ❌ УБИРАЕМ onNewMessage - обработка теперь только в useChatMessagesSimple
    onStatusChange: (data) => {
      logger.debug('🔄 Статус заказа изменен:', data);
      // Обновляем статус заказа
    },
    onConnectionChange: (status) => {
      logger.debug(`🔌 WebSocket статус: ${status}`);
    }
  });

  // Уведомления
  const { showNotification, playSound } = useChatNotifications();



  // Сброс активного чата при смене orderId
  const prevOrderIdRef = useRef<string | undefined>(orderId);
  const markingAsReadRef = useRef<Set<string>>(new Set()); // Защита от повторных вызовов
  
  // Защищенная функция markAsRead
  const markAsReadSafe = useCallback(async (chatId: string) => {
    if (markingAsReadRef.current.has(chatId)) {
      console.log(`🔍 markAsRead уже выполняется для чата ${chatId}, пропускаем`);
      return;
    }
    
    markingAsReadRef.current.add(chatId);
    try {
      console.log(`🔍 Выполняем markAsRead для чата ${chatId}`);
      await markAsRead(chatId);
    } finally {
      // Убираем из set через небольшую задержку
      setTimeout(() => {
        markingAsReadRef.current.delete(chatId);
      }, 1000);
    }
  }, [markAsRead]);
  
  useEffect(() => {
    const orderIdChanged = prevOrderIdRef.current !== orderId;

    // Сбрасываем активный чат ТОЛЬКО при реальной смене URL orderId
    if (orderIdChanged && activeChat && orderId && activeChat.order_id !== orderId) {
      logger.debug(`🔄 Сброс активного чата при смене orderId: ${activeChat.order_id} → ${orderId}`);
      clearActiveChat();
      setActiveView('list');
      setUserReturnedToList(false); // Сбрасываем флаг при смене заказа
      setHasAutoOpenedOrderChat(false); // Сбрасываем флаг автооткрытия при смене заказа
    }

    // 🔥 СБРАСЫВАЕМ ФЛАГ АВТООТКРЫТИЯ ПРИ УХОДЕ СО СТРАНИЦЫ ЗАКАЗА
    if (orderIdChanged && !orderId) {
      setHasAutoOpenedOrderChat(false);
      setUserReturnedToList(false);
    }

    prevOrderIdRef.current = orderId;
  }, [orderId, activeChat, clearActiveChat]);

  // 🔥 СЛУШАЕМ СОБЫТИЕ О ГОТОВНОСТИ ЧАТА ЗАКАЗА
  useEffect(() => {
    const handleChatReady = (event: CustomEvent) => {
      const { orderId: readyOrderId } = event.detail;
      if (readyOrderId === orderId) {
        logger.debug(`🎯 Чат для заказа ${orderId} готов к использованию`);
        // Обновляем список чатов чтобы подхватить новый чат
        loadChats();
      }
    };

    window.addEventListener('orderChatReady', handleChatReady as EventListener);
    
    return () => {
      window.removeEventListener('orderChatReady', handleChatReady as EventListener);
    };
  }, [orderId, loadChats]);

  // 🎯 АВТОМАТИЧЕСКОЕ ОТКРЫТИЕ ЧАТА при изменении orderId
  useEffect(() => {
    if (orderId && isVisible && !userReturnedToList && !hasAutoOpenedOrderChat) {
      // 🔥 НЕ переключаемся на вид чата сразу - дождемся загрузки
      
      const openOrderChat = async () => {
        try {
          // Сначала показываем лоадер
          setIsChatLoading(true);
          
          // Загружаем чаты
          await loadChats();
          
          // Ждем немного чтобы state обновился
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Ищем чат заказа или общий чат
          console.log(`🔍 Ищем чат для orderId: ${orderId}, всего чатов: ${chats.length}`);
          console.log(`🔍 Доступные чаты:`, chats.map(c => ({ id: c.id, order_id: c.order_id, title: c.title })));
          
          let chat = chats.find((c: Chat) => 
            c.order_id === orderId || (orderId === 'telegram_general' && c.id === 'telegram_general')
          );
          
          console.log(`🔍 Найденный чат:`, chat ? { id: chat.id, order_id: chat.order_id, title: chat.title } : 'НЕ НАЙДЕН');
          
          if (!chat) {
            // Ищем чат который должен уже существовать
            console.log(`⚠️ Чат не найден, пробуем findOrderChat для ${orderId}`);
            const foundChat = await findOrderChat(orderId);
            chat = foundChat || undefined;
            console.log(`🔍 findOrderChat результат:`, foundChat ? { id: foundChat.id, title: foundChat.title } : 'NULL');
          }
          
          if (chat) {
            // Если уже этот чат активен и есть сообщения в текущей сессии — показываем без лоадера
            if (activeChat?.id === chat.id && messages.length > 0) {
              setActiveView('chat'); // Переключаемся на вид чата
              setHasAutoOpenedOrderChat(true);
              // Прокрутка к последнему сообщению
              setTimeout(() => {
                const messagesContainer = document.querySelector('.chat-widget-messages');
                if (messagesContainer) {
                  messagesContainer.scrollTop = messagesContainer.scrollHeight;
                }
              }, 200);
              return;
            }

            // Загружаем чат
            await selectChat(chat.id);
            setActiveChatId(chat.id);
            
            // 🔥 ТОЛЬКО ПОСЛЕ ЗАГРУЗКИ переключаемся на вид чата
            setActiveView('chat');
            
            // ✅ АВТОМАТИЧЕСКИ СБРАСЫВАЕМ СЧЕТЧИК при автоматическом открытии чата заказа
            if (chat.unreadCount > 0) {
              console.log(`🔍 Отмечаем чат ${chat.id} как прочитанный (unreadCount: ${chat.unreadCount})`);
              await markAsReadSafe(chat.id);
            } else {
              console.log(`🔍 Чат ${chat.id} уже прочитан (unreadCount: ${chat.unreadCount})`);
            }
            onChatSelect?.(chat);
            
            // 🔥 ОТМЕЧАЕМ ЧТО ЧАТ ЗАКАЗА БЫЛ АВТОМАТИЧЕСКИ ОТКРЫТ
            setHasAutoOpenedOrderChat(true);
            
            // Принудительная прокрутка к последнему сообщению
            setTimeout(() => {
              const messagesContainer = document.querySelector('.chat-widget-messages');
              if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
              }
            }, 200);
          } else {
            // Если чат не найден, возвращаемся к списку
            console.error(`❌ Чат для заказа ${orderId} не найден`);
            console.error(`❌ Доступные чаты на момент поиска:`, chats.map(c => ({ id: c.id, order_id: c.order_id, title: c.title })));
            setActiveView('list');
            
            // Проверяем статус заказа из глобального кеша
            const { orders } = useOrdersStore.getState();
            const orderFromCache = orders.find(o => String(o.id) === String(orderId));
            
            if (orderFromCache?.status === 'new') {
              showNotification('Чат создается... Попробуйте через несколько секунд.', 'info');
            } else {
              showNotification('Чат не найден. Убедитесь что заказ был открыт для просмотра.', 'warning');
            }
          }
        } catch (error) {
          logger.error('❌ Ошибка автоматического открытия чата заказа:', error);
          setActiveView('list');
          // Сбрасываем все состояния загрузки при ошибке
          setIsChatLoading(false);
        } finally {
          setIsChatLoading(false);
        }
      };
      
      openOrderChat();
    }
    // Убрано принудительное возвращение к списку при отсутствии orderId,
    // чтобы не перебивать ручной выбор диалога
  }, [orderId, isVisible, loadChats, findOrderChat, selectChat, onChatSelect, userReturnedToList, hasAutoOpenedOrderChat, showNotification, markAsReadSafe]);


  // Показать виджет
  const handleShow = useCallback(async () => {
    setIsVisible(true);
    
    // Если есть orderId (на странице заказа), сбрасываем флаги для автоматического открытия
    if (orderId) {
      setUserReturnedToList(false);
      setHasAutoOpenedOrderChat(false);
    }
  }, [orderId]);

  // Скрыть виджет
  const handleHide = useCallback(() => {
    setIsVisible(false);
    setActiveView('list');
    setActiveChatId(null);
    clearActiveChat();
    // 🔥 СБРАСЫВАЕМ флаг автоматического открытия при закрытии виджета
    setHasAutoOpenedOrderChat(false);
    // 🔥 ИСПРАВЛЕНИЕ: НЕ сбрасываем userReturnedToList при закрытии виджета!
    // Если пользователь вернулся к списку, он должен остаться в списке при следующем открытии
    // setUserReturnedToList(false); // УБРАНО!
  }, [clearActiveChat]);



  // Выбор чата из списка
  const handleChatSelect = useCallback(async (chat: Chat) => {
    try {
      setIsChatLoading(true);
      
      // 🔥 ИСПРАВЛЕНИЕ: НЕ переключаемся на вид чата до успешной загрузки
      
      // Устанавливаем получателя в зависимости от типа чата
      if (chat.chatType === 'telegram_general') {
        setSelectedRecipient('manager'); // Для общего чата всегда менеджер
      } else {
        setSelectedRecipient(null); // Для чатов заказов пользователь должен выбрать
      }
      
      // Сначала загружаем чат
      await selectChat(chat.id);
      
      // 🔥 ТОЛЬКО ПОСЛЕ УСПЕШНОЙ ЗАГРУЗКИ переключаемся на вид чата
      setActiveView('chat');
      
      // ✅ АВТОМАТИЧЕСКИ СБРАСЫВАЕМ СЧЕТЧИК при открытии диалога (стандартный UX)
      if (chat.unreadCount > 0) {
        await markAsReadSafe(chat.id);
      }
      onChatSelect?.(chat);
      
      // Принудительная прокрутка к последнему сообщению
      setTimeout(() => {
        const messagesContainer = document.querySelector('.chat-widget-messages');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 300);
    } catch (error) {
      logger.error('❌ Ошибка выбора чата:', error);
      showNotification('Не удалось открыть чат', 'error');
      // 🔥 ИСПРАВЛЕНИЕ: Убеждаемся что остаемся в списке при ошибке
      setActiveView('list');
      // Сбрасываем все состояния загрузки при ошибке
      setIsChatLoading(false);
    } finally {
      setIsChatLoading(false);
    }
  }, [selectChat, onChatSelect, showNotification, markAsReadSafe]); // Добавили markAsReadSafe обратно в зависимости

  // Возврат к списку чатов
  const handleBackToList = useCallback(() => {
    setActiveView('list');
    setUserReturnedToList(true); // Отмечаем что пользователь вручную вернулся к списку
    // Сбрасываем активный чат и его состояния
    clearActiveChat();
  }, [clearActiveChat]);

  // Обработчик клика по сообщению убран - не нужен для счетчика
  // Счетчик сбрасывается автоматически при открытии диалога

  // Отправка сообщения
  const handleSendMessage = useCallback(async (content: string, files?: File[]) => {
    if (!activeChat) return;

    // Валидация получателя - требуем выбор только для чатов заказов
    if (!selectedRecipient && activeChat.chatType !== 'telegram_general') {
      showNotification('Выберите получателя сообщения', 'warning');
      // НЕ очищаем поле ввода - пользователь должен выбрать получателя
      return;
    }

    // Политика: нельзя отправлять файлы без текста
    const trimmed = (content || '').trim();
    if (files && files.length > 0 && !trimmed) {
      showNotification('Добавьте текст к файлам. Пустые сообщения с файлами запрещены.', 'warning');
      return;
    }

    try {
      let message: Message;
      
      if (files && files.length > 0) {
        // Показываем индикатор загрузки файлов
        setIsUploadingFiles(true);
        message = await sendFileMessage(activeChat.id, files, selectedRecipient!, trimmed);
        setIsUploadingFiles(false);
      } else {
        message = await sendMessage(activeChat.id, content, selectedRecipient!);
      }

      onMessageSent?.(message);
      playSound();
      
      // Сбрасываем выбор получателя ТОЛЬКО после успешной отправки
      setSelectedRecipient(null);
      
      // Возвращаем true чтобы MessageInput знал что сообщение отправлено
      return true;
    } catch (error) {
      logger.error('❌ Ошибка отправки сообщения:', error);
      showNotification('Не удалось отправить сообщение', 'error');
      // Сбрасываем состояние загрузки при ошибке
      setIsUploadingFiles(false);
      // Возвращаем false чтобы MessageInput НЕ очищал поле
      return false;
    }
  }, [activeChat, selectedRecipient, sendMessage, sendFileMessage, onMessageSent, playSound, showNotification]);

  // Обработчик клика по кнопке "Заказ"
  const handleOrderButtonClick = useCallback(() => {
    const chatOrderId = activeChat?.order_id;
    if (!chatOrderId) {
      logger.warn('❌ Нет order_id в активном чате для навигации к деталям заказа');
      return;
    }

    try {
      // Определяем правильный маршрут в зависимости от режима приложения
      const orderPath = isTelegramMode 
        ? `/miniapp/orders/${chatOrderId}` 
        : `/orders/${chatOrderId}`;
      
      logger.debug(`🔄 Навигация к деталям заказа: ${orderPath}`);
      
      // Закрываем виджет чата перед навигацией
      setIsVisible(false);
      setActiveView('list');
      
      // Выполняем навигацию
      navigate(orderPath);
    } catch (error) {
      logger.error('❌ Ошибка навигации к деталям заказа:', error);
    }
  }, [activeChat?.order_id, isTelegramMode, navigate]);

  // Убрали сложную логику клавиатуры - теперь используем CSS и viewport units

  if (!isVisible) {
    return (
      <div className={className}>
        <button
          onClick={handleShow}
          className="chat-widget-button"
          aria-label="Открыть чат"
        >
          <svg className="chat-widget-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          
          {/* Бейдж с количеством непрочитанных */}
          {unreadCount > 0 && (
            <div className={`chat-widget-badge ${unreadCount > 0 ? 'new-message' : ''}`}>
              {/* 🔥 ВРЕМЕННО: Показываем реальное значение из store */}
              {counts.unread_messages > 99 ? '99+' : counts.unread_messages}
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`chat-widget-modal show ${className}`}>
      <div className="chat-widget-overlay" onClick={handleHide} />
      <div className="chat-widget-container">
        
        {/* Заголовок - скрываем во время загрузки */}
        {!isChatLoading && (
          <>
            {activeView === 'list' ? (
          <div className="chat-widget-header">
            <div className="flex items-center space-x-2">
              <h3>Чаты</h3>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Кнопка управления звуком */}
              <SoundToggleButton />
              
              <button
                onClick={handleHide}
                className="chat-widget-close"
                aria-label="Закрыть чат"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="chat-widget-chat-header">
            <button
              onClick={handleBackToList}
              className="chat-widget-back-btn"
              aria-label="Назад к списку чатов"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="chat-widget-avatar">
              {activeChat?.title ? activeChat.title.substring(0, 2).toUpperCase() : 'ЧТ'}
              <div className={`chat-status status-${activeChat?.status || 'new'}`}>
                <ion-icon 
                  name={getStatusIcon(activeChat?.status || 'new')}
                  style={{ fontSize: '10px' }}
                />
              </div>
            </div>
            
            <div className="chat-widget-info">
              <h4 className="chat-widget-title">{activeChat?.title || 'Чат'}</h4>
            </div>
            
            {activeChat?.order_id && (
              <button 
                className="chat-widget-order-btn" 
                title="Перейти к деталям заказа"
                onClick={handleOrderButtonClick}
              >
                <span>Заказ</span>
              </button>
            )}
            
            <button
              onClick={handleHide}
              className="chat-widget-close"
              aria-label="Закрыть чат"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
            )}
          </>
        )}

        {/* Содержимое */}
        <div className="chat-widget-content relative">
          {/* 🔥 ЕДИНЫЙ ЛОАДЕР: показываем красивый лоадер при любой загрузке */}
          {(isChatLoading || (isLoading && chats.length === 0)) ? (
            <ChatLoader 
              message={isChatLoading ? "Загрузка сообщений..." : "Загрузка чатов..."}
              subtitle={isChatLoading ? "Подключение к чату" : "Получение списка заказов"}
            />
          ) : (
            <>
              {activeView === 'list' ? (
                <ChatList
                  chats={chats}
                  isLoading={false}
                  onChatSelect={handleChatSelect}
                  onLoadMore={loadChats}
                />
              ) : (
                <>
              {/* Список сообщений */}
              <MessageList
                messages={messages}
                isLoading={false}
                orderStatus={activeChat?.status}
                hasMoreMessages={messagesHasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMoreMessages}
                orderId={activeChat?.order_id || orderId}
              />
              
              {/* Контейнер для поля ввода и кнопок получателя */}
              <div className="chat-widget-input-section">
                {/* Выбор получателя - показываем только для чатов заказов, НЕ для общего чата */}
                {activeChat && activeChat.chatType !== 'telegram_general' && (
                  <RecipientSelector
                    selectedRecipient={selectedRecipient}
                    onRecipientChange={setSelectedRecipient}
                  />
                )}
                
                {/* Поле ввода */}
                <MessageInput
                  onSendMessage={handleSendMessage}
                  disabled={!isConnected || isUploadingFiles}
                  isUploading={isUploadingFiles}
                  showFileButton={activeChat?.chatType !== 'telegram_general'} // Скрываем кнопку файлов для общего чата
                  placeholder={
                    isUploadingFiles 
                      ? 'Загрузка файлов...'
                      : activeChat?.chatType === 'telegram_general' 
                        ? 'Сообщение менеджеру...' 
                        : selectedRecipient === 'manager' 
                          ? 'Сообщение менеджеру...' 
                          : selectedRecipient === 'technologist'
                            ? 'Сообщение технологу...'
                            : 'Введите сообщение...'
                  }
                />
              </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
